import React, { useState, useEffect, useMemo } from 'react';
import {
  FaSync, FaPlus, FaEdit, FaTrash, FaSearch,
  FaCalendarDay, FaArrowUp, FaArrowDown, FaCoins,
  FaChevronLeft, FaChevronRight
} from 'react-icons/fa';
import axiosInstance from '../utils/axiosInstance';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

const KASALAR   = ['FAYNPAY', 'NEOPAYS', 'ULTRAPAY', 'FULGUR', 'ANINDA', 'MİNİ', 'ENHİZLİ', 'DIS_KASA'];
const AY_ADLARI = ['', 'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
                   'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const fmt = (n) => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2 }).format(n ?? 0);
const BOSH = { tarih: dayjs().format('YYYY-MM-DD'), kasa: '', yontem: '', yatirim: 0, cekim: 0, komisyon: 0 };

export default function GunlukHareketler() {
  const bugun = dayjs();
  const [kayitlar, setKayitlar] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [filtre, setFiltre]     = useState({
    kasa: '', ay: bugun.month() + 1, yil: bugun.year(), gun: '', yontem: '',
  });
  const [modal, setModal]   = useState(null);
  const [secili, setSecili] = useState(null);
  const [form, setForm]     = useState(BOSH);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(
        Object.fromEntries(Object.entries(filtre).filter(([, v]) => v !== ''))
      );
      const res = await axiosInstance.get(`/gunluk/all?${params}`);
      setKayitlar(res.data);
    } catch { toast.error('Veriler yüklenemedi!'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [filtre.ay, filtre.yil, filtre.kasa, filtre.gun, filtre.yontem]);

  const handleEkle    = () => { setForm(BOSH); setSecili(null); setModal('ekle'); };
  const handleDuzenle = (k) => {
    setSecili(k);
    setForm({ tarih: dayjs(k.tarih?.full).format('YYYY-MM-DD'), kasa: k.kasa, yontem: k.yontem ?? '', yatirim: k.yatirim, cekim: k.cekim, komisyon: k.komisyon });
    setModal('duzenle');
  };
  const handleKaydet = async () => {
    try {
      if (modal === 'ekle') { await axiosInstance.post('/gunluk/add', form); toast.success('Kayıt eklendi.'); }
      else { await axiosInstance.patch(`/gunluk/update/${secili._id}`, form); toast.success('Güncellendi.'); }
      setModal(null); fetchData();
    } catch { toast.error('İşlem başarısız!'); }
  };
  const handleSil = async (id) => {
    if (!window.confirm('Bu kayıt silinsin mi?')) return;
    try { await axiosInstance.delete(`/gunluk/delete/${id}`); toast.success('Silindi.'); fetchData(); }
    catch { toast.error('Silme başarısız!'); }
  };

  const filtered = useMemo(() =>
    kayitlar.filter(k =>
      !search ||
      (k.kasa ?? '').toLowerCase().includes(search.toLowerCase()) ||
      (k.yontem ?? '').toLowerCase().includes(search.toLowerCase())
    ), [kayitlar, search]);

  const ozet = useMemo(() => {
    const t = { yatirim: 0, cekim: 0, komisyon: 0 };
    filtered.forEach(k => { t.yatirim += k.yatirim ?? 0; t.cekim += k.cekim ?? 0; t.komisyon += k.komisyon ?? 0; });
    return t;
  }, [filtered]);

  const ayGunSayisi = dayjs(`${filtre.yil}-${filtre.ay}-01`).daysInMonth();
  const gunler = Array.from({ length: ayGunSayisi }, (_, i) => i + 1);
  const setGun = (g) => setFiltre(p => ({ ...p, gun: p.gun === String(g) ? '' : String(g) }));
  const prevAy = () => { const d = dayjs(`${filtre.yil}-${filtre.ay}-01`).subtract(1, 'month'); setFiltre(p => ({ ...p, ay: d.month() + 1, yil: d.year(), gun: '' })); };
  const nextAy = () => { const d = dayjs(`${filtre.yil}-${filtre.ay}-01`).add(1, 'month'); setFiltre(p => ({ ...p, ay: d.month() + 1, yil: d.year(), gun: '' })); };

  const gunlerdeKayit = useMemo(() => {
    const s = new Set(); kayitlar.forEach(k => { if (k.tarih?.gun) s.add(k.tarih.gun); }); return s;
  }, [kayitlar]);

  return (
    <div style={S.page}>

      {/* HEADER */}
      <div style={S.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={S.iconBox}><FaCalendarDay /></div>
          <div>
            <h2 style={S.title}>Günlük Hareketler</h2>
            <p style={S.subtitle}>
              {AY_ADLARI[filtre.ay]} {filtre.yil}
              {filtre.gun ? ` • ${filtre.gun}. gün` : ''}
              <span style={S.countBadge}>{filtered.length} kayıt</span>
            </p>
          </div>
        </div>
        <div style={S.hRight}>
          <select style={S.sel} value={filtre.kasa} onChange={e => setFiltre(p => ({ ...p, kasa: e.target.value }))}>
            <option value="">Tüm Kasalar</option>
            {KASALAR.map(k => <option key={k} value={k}>{k}</option>)}
          </select>
          <button style={S.syncBtn} onClick={fetchData}>
            <FaSync style={loading ? { animation: 'spin 0.8s linear infinite' } : {}} />
          </button>
          <button style={S.addBtn} onClick={handleEkle}><FaPlus /> Ekle</button>
        </div>
      </div>

      {/* ÖZET */}
      <div style={S.statRow}>
        {[
          { icon: <FaArrowUp />,  label: 'Yatırım',  value: fmt(ozet.yatirim),  color: '#22c55e' },
          { icon: <FaArrowDown />, label: 'Çekim',   value: fmt(ozet.cekim),    color: '#ef4444' },
          { icon: <FaCoins />,    label: 'Komisyon', value: fmt(ozet.komisyon), color: '#f59e0b' },
          { icon: <FaCoins />,    label: 'Net',
            value: fmt(ozet.yatirim - ozet.cekim - ozet.komisyon),
            color: (ozet.yatirim - ozet.cekim - ozet.komisyon) >= 0 ? '#22c55e' : '#ef4444' },
        ].map(({ icon, label, value, color }) => (
          <div key={label} style={S.statCard}>
            <div style={{ ...S.statIcon, color, background: `${color}15` }}>{icon}</div>
            <div>
              <p style={S.statLbl}>{label}</p>
              <p style={{ ...S.statVal, color }}>{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* TAKVİM */}
      <div style={S.calWrap}>
        <div style={S.calHead}>
          <button style={S.calNav} onClick={prevAy}><FaChevronLeft /></button>
          <span style={S.calTitle}>{AY_ADLARI[filtre.ay]} {filtre.yil}</span>
          <button style={S.calNav} onClick={nextAy}><FaChevronRight /></button>
          {filtre.gun && (
            <button style={S.clearBtn} onClick={() => setFiltre(p => ({ ...p, gun: '' }))}>
              {filtre.gun}. gün × temizle
            </button>
          )}
        </div>
        <div style={S.gunGrid}>
          {gunler.map(g => {
            const aktif   = filtre.gun === String(g);
            const kayitli = gunlerdeKayit.has(g);
            return (
              <button key={g} onClick={() => setGun(g)} style={{
                ...S.gunBtn,
                ...(aktif    ? S.gunAktif    : {}),
                ...(kayitli && !aktif ? S.gunKayitli : {}),
              }}>
                {g}
                {kayitli && <span style={{ ...S.gunDot, background: aktif ? '#fff' : '#f27127' }} />}
              </button>
            );
          })}
        </div>
      </div>

      {/* TABLO */}
      <div style={S.tableCard}>
        <div style={S.tHead}>
          <div style={S.searchWrap}>
            <FaSearch style={S.searchIco} />
            <input style={S.searchInp} placeholder="Kasa veya yöntem ara..." value={search}
              onChange={e => setSearch(e.target.value)} />
          </div>
        </div>
        {loading ? <Spinner /> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead>
                <tr>{['Tarih', 'Kasa', 'Yöntem', 'Yatırım', 'Çekim', 'Komisyon', ''].map(h =>
                  <th key={h} style={S.th}>{h}</th>
                )}</tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={7} style={S.empty}>
                    {filtre.gun ? `${filtre.gun}. gün için kayıt bulunamadı` : 'Kayıt bulunamadı'}
                  </td></tr>
                ) : filtered.map(k => (
                  <tr key={k._id} style={S.tr}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(242,113,39,0.03)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={S.td}>
                      <span style={S.dateDay}>{k.tarih?.gun}</span>
                      <span style={S.dateMon}> {AY_ADLARI[k.tarih?.ay]}</span>
                    </td>
                    <td style={S.td}><span style={S.kasaBadge}>{k.kasa}</span></td>
                    <td style={{ ...S.td, color: '#64748b', fontSize: '0.82rem' }}>{k.yontem || '—'}</td>
                    <td style={{ ...S.td, color: '#22c55e', fontWeight: 700 }}>{fmt(k.yatirim)}</td>
                    <td style={{ ...S.td, color: '#ef4444', fontWeight: 700 }}>{fmt(k.cekim)}</td>
                    <td style={{ ...S.td, color: '#f59e0b', fontWeight: 700 }}>{fmt(k.komisyon)}</td>
                    <td style={S.td}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button style={S.editBtn} onClick={() => handleDuzenle(k)}><FaEdit /></button>
                        <button style={S.delBtn}  onClick={() => handleSil(k._id)}><FaTrash /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              {filtered.length > 0 && (
                <tfoot>
                  <tr style={{ borderTop: '2px solid #1a1f27' }}>
                    <td colSpan={3} style={{ ...S.td, color: '#334155', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase' }}>
                      Toplam • {filtered.length} kayıt
                    </td>
                    <td style={{ ...S.td, color: '#22c55e', fontWeight: 800 }}>{fmt(ozet.yatirim)}</td>
                    <td style={{ ...S.td, color: '#ef4444', fontWeight: 800 }}>{fmt(ozet.cekim)}</td>
                    <td style={{ ...S.td, color: '#f59e0b', fontWeight: 800 }}>{fmt(ozet.komisyon)}</td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>

      {/* MODAL */}
      {modal && (
        <div style={S.overlay} onClick={() => setModal(null)}>
          <div style={S.modalBox} onClick={e => e.stopPropagation()}>
            <div style={S.mHead}>
              <h3 style={S.mTitle}>
                {modal === 'ekle'
                  ? <><FaPlus style={{ color: '#f27127' }} /> Yeni Kayıt</>
                  : <><FaEdit style={{ color: '#3b82f6' }} /> Kaydı Düzenle</>}
              </h3>
              <button style={S.closeBtn} onClick={() => setModal(null)}>×</button>
            </div>
            <div style={S.mBody}>
              <div style={S.formGrid}>
                <FG label="Tarih"><input style={S.inp} type="date" value={form.tarih} onChange={e => setForm(p => ({ ...p, tarih: e.target.value }))} /></FG>
                <FG label="Kasa">
                  <select style={S.inp} value={form.kasa} onChange={e => setForm(p => ({ ...p, kasa: e.target.value }))}>
                    <option value="">Seçin</option>
                    {KASALAR.map(k => <option key={k} value={k}>{k}</option>)}
                  </select>
                </FG>
                <FG label="Yöntem"><input style={S.inp} value={form.yontem} placeholder="HAYDİPAY, HAVALE..." onChange={e => setForm(p => ({ ...p, yontem: e.target.value }))} /></FG>
                <FG label="Yatırım (TL)"><input style={S.inp} type="number" value={form.yatirim} onChange={e => setForm(p => ({ ...p, yatirim: parseFloat(e.target.value) || 0 }))} /></FG>
                <FG label="Çekim (TL)"><input style={S.inp} type="number" value={form.cekim} onChange={e => setForm(p => ({ ...p, cekim: parseFloat(e.target.value) || 0 }))} /></FG>
                <FG label="Komisyon (TL)"><input style={S.inp} type="number" value={form.komisyon} onChange={e => setForm(p => ({ ...p, komisyon: parseFloat(e.target.value) || 0 }))} /></FG>
              </div>
              <div style={S.mFoot}>
                <button style={S.cancelBtn} onClick={() => setModal(null)}>İptal</button>
                <button style={S.saveBtn} onClick={handleKaydet}>Kaydet</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin   { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
        @keyframes fadeIn { from { opacity:0; } to { opacity:1; } }
      `}</style>
    </div>
  );
}

const FG = ({ label, children }) => (
  <div>
    <label style={S.fLabel}>{label}</label>
    {children}
  </div>
);
const Spinner = () => (
  <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
    <div style={S.spinner} />
  </div>
);

const S = {
  page:      { padding: 30, backgroundColor: '#090a0c', minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#e2e8f0', animation: 'fadeUp 0.3s ease' },
  header:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16, marginBottom: 22, background: 'linear-gradient(135deg,#0f1115,#13171e)', border: '1px solid #1a1f27', borderRadius: 18, padding: '20px 26px', boxShadow: '0 8px 32px rgba(0,0,0,0.35)' },
  iconBox:   { width: 44, height: 44, borderRadius: 13, background: 'rgba(242,113,39,0.12)', color: '#f27127', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0 },
  title:     { color: '#fff', fontSize: '1.3rem', fontWeight: 800, margin: 0, letterSpacing: '-0.3px' },
  subtitle:  { color: '#475569', fontSize: '0.8rem', margin: '3px 0 0', display: 'flex', alignItems: 'center', gap: 8 },
  countBadge:{ background: 'rgba(242,113,39,0.12)', color: '#f27127', padding: '1px 9px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700 },
  hRight:    { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  sel:       { background: '#0a0d10', border: '1px solid #1a1f27', borderRadius: 10, color: '#94a3b8', padding: '9px 12px', fontSize: '0.82rem', cursor: 'pointer', outline: 'none' },
  syncBtn:   { width: 38, height: 38, borderRadius: 10, background: 'rgba(242,113,39,0.08)', border: '1px solid rgba(242,113,39,0.2)', color: '#f27127', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  addBtn:    { display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 10, background: 'linear-gradient(135deg,#f27127,#ff9a44)', color: '#fff', border: 'none', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', boxShadow: '0 4px 14px rgba(242,113,39,0.25)' },

  statRow:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(190px,1fr))', gap: 12, marginBottom: 20 },
  statCard:  { background: '#0f1115', border: '1px solid #1a1f27', borderRadius: 14, padding: '15px 18px', display: 'flex', alignItems: 'center', gap: 12 },
  statIcon:  { width: 38, height: 38, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.95rem', flexShrink: 0 },
  statLbl:   { color: '#334155', fontSize: '0.67rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 3px' },
  statVal:   { fontSize: '1.05rem', fontWeight: 800, margin: 0 },

  calWrap:   { background: '#0f1115', border: '1px solid #1a1f27', borderRadius: 16, padding: '16px 20px', marginBottom: 18 },
  calHead:   { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 },
  calTitle:  { color: '#94a3b8', fontWeight: 700, fontSize: '0.88rem', flex: 1 },
  calNav:    { background: 'none', border: '1px solid #1a1f27', color: '#334155', borderRadius: 7, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '0.65rem' },
  clearBtn:  { background: 'rgba(242,113,39,0.08)', border: '1px solid rgba(242,113,39,0.2)', color: '#f27127', borderRadius: 8, padding: '4px 12px', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer' },
  gunGrid:   { display: 'flex', flexWrap: 'wrap', gap: 5 },
  gunBtn:    { width: 34, height: 34, borderRadius: 8, background: '#0a0d10', border: '1px solid #1a1f27', color: '#334155', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.12s' },
  gunKayitli:{ borderColor: 'rgba(242,113,39,0.3)', color: '#64748b' },
  gunAktif:  { background: '#f27127', borderColor: '#f27127', color: '#fff', boxShadow: '0 0 10px rgba(242,113,39,0.35)' },
  gunDot:    { position: 'absolute', bottom: 2, right: 2, width: 4, height: 4, borderRadius: '50%' },

  tableCard: { background: '#0f1115', border: '1px solid #1a1f27', borderRadius: 16, overflow: 'hidden' },
  tHead:     { padding: '14px 18px', borderBottom: '1px solid #1a1f27' },
  searchWrap:{ position: 'relative', maxWidth: 320 },
  searchIco: { position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#1e2a38', fontSize: '0.8rem' },
  searchInp: { width: '100%', padding: '9px 12px 9px 34px', background: '#0a0d10', border: '1px solid #1a1f27', borderRadius: 9, color: '#e2e8f0', fontSize: '0.83rem', boxSizing: 'border-box', outline: 'none' },
  table:     { width: '100%', borderCollapse: 'collapse' },
  th:        { padding: '11px 16px', background: 'rgba(255,255,255,0.008)', color: '#1e2a38', fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1.3, borderBottom: '1px solid #1a1f27', textAlign: 'left', whiteSpace: 'nowrap' },
  tr:        { borderBottom: '1px solid rgba(255,255,255,0.02)', transition: 'background 0.12s' },
  td:        { padding: '12px 16px', fontSize: '0.85rem', color: '#94a3b8' },
  empty:     { textAlign: 'center', padding: 56, color: '#1e2a38', fontStyle: 'italic' },
  dateDay:   { color: '#e2e8f0', fontWeight: 800 },
  dateMon:   { color: '#334155', fontSize: '0.75rem' },
  kasaBadge: { background: 'rgba(242,113,39,0.08)', color: '#f27127', padding: '3px 9px', borderRadius: 6, fontSize: '0.76rem', fontWeight: 700 },
  editBtn:   { width: 29, height: 29, borderRadius: 7, border: 'none', background: 'rgba(59,130,246,0.08)', color: '#3b82f6', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem' },
  delBtn:    { width: 29, height: 29, borderRadius: 7, border: 'none', background: 'rgba(239,68,68,0.08)',  color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem' },

  overlay:   { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, animation: 'fadeIn 0.2s ease' },
  modalBox:  { background: '#0f1115', border: '1px solid #1a1f27', borderRadius: 20, width: '92%', maxWidth: 550, boxShadow: '0 30px 60px rgba(0,0,0,0.6)', animation: 'fadeUp 0.22s ease' },
  mHead:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '17px 22px', borderBottom: '1px solid #1a1f27' },
  mTitle:    { color: '#fff', fontSize: '0.97rem', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 8 },
  closeBtn:  { background: 'none', border: 'none', color: '#334155', fontSize: '1.5rem', cursor: 'pointer', lineHeight: 1 },
  mBody:     { padding: 22 },
  formGrid:  { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 },
  fLabel:    { color: '#334155', fontSize: '0.65rem', fontWeight: 700, display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.8 },
  inp:       { width: '100%', padding: '10px 12px', background: '#0a0d10', border: '1px solid #1a1f27', borderRadius: 9, color: '#e2e8f0', fontSize: '0.86rem', boxSizing: 'border-box', outline: 'none' },
  mFoot:     { display: 'flex', gap: 10, justifyContent: 'flex-end' },
  cancelBtn: { padding: '9px 18px', borderRadius: 9, background: '#1a1f27', color: '#64748b', border: 'none', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' },
  saveBtn:   { padding: '9px 20px', borderRadius: 9, background: 'linear-gradient(135deg,#f27127,#ff9a44)', color: '#fff', border: 'none', fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem' },
  spinner:   { width: 32, height: 32, borderRadius: '50%', border: '3px solid #1a1f27', borderTopColor: '#f27127', animation: 'spin 0.7s linear infinite' },
};
